import { Box } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit } from 'react-icons/md'
import { hasPermission } from 'src/components/common/Authorization'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { permissionValue } from 'src/enums/permissionValue'
import { IEmptyStatusProps } from 'src/generated-types/empty-status'
import { ITimeReport } from 'src/generated-types/time-report'
import {
  invalidateTimeReportCache,
  timeReportsEndpoints,
  useDeleteTimeReport,
  useUpdateTimeReport,
} from '../../../api/time-report'
import NoTimeReports from '../../../assets/svg/no-time-reports.svg'
import { useDialogCtx, useLocationCtx } from '../../../hooks/context-hooks'
import TimeReportForm from './TimeReportForm'
import { DateTimeStackCell, DurationCell, formatDuration } from './TimeReportCells'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'

interface ITimeReportTable extends IEmptyStatusProps {
  disabledButton?: boolean
  scheduleTaskId: EntityId
  customerId: EntityId
  onItemsChange?: (items: ITimeReport[]) => void
  height?: number
  rowsToFit?: { min?: number; max?: number; fill?: boolean }
}

export const TimeReportTable: React.FC<ITimeReportTable> = ({
  scheduleTaskId,
  customerId,
  disabledButton,
  emptyStatusContent,
  emptyStatusTitle,
  emptyStatusImageWidth,
  emptyStatusContentFontSize,
  emptyStatusTitleFontSize,
  onItemsChange,
  height = 500,
  rowsToFit,
}) => {
  const { t: translate } = useTranslation(['time-reports', 'common', 'task'])
  const [selectedId, setSelectedId] = useState(0)
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { permission } = useLocationCtx()
  const { mutateAsync: updateMutate } = useUpdateTimeReport(scheduleTaskId, selectedId)
  const { mutateAsync: deleteMutate } = useDeleteTimeReport(scheduleTaskId)

  const onUpdate = useCallback(
    row => {
      setSelectedId(row.id)
      showDialog(TimeReportForm, {
        title: translate('updateTimeReport'),
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        componentProps: {
          customerId: customerId,
          onSubmit: async values => {
            await updateMutate(values)
            invalidateTimeReportCache.useGetTimeReports(scheduleTaskId)
          },
          initialValues: {
            ...row,
          },
          isUpdateForm: true,
        },
      })
    },
    [customerId, scheduleTaskId, showDialog, translate, updateMutate],
  )

  const onDelete = useCallback(
    async row => {
      showConfirmDialog(
        translate('deleteTimeReportTitle'),
        translate('deleteTimeReportMessage'),
        {
          acceptText: translate('yes', { ns: 'common' }),
          cancelText: translate('no', { ns: 'common' }),
        },
        async pop => {
          await deleteMutate(row.id)
          invalidateTimeReportCache.useGetTimeReports(scheduleTaskId)
          pop()
        },
      )
    },
    [deleteMutate, scheduleTaskId, showConfirmDialog, translate],
  )

  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        field: 'description',
        headerName: translate('description', { ns: 'common' }),
        flex: 1.5,
        minWidth: 200,
        cellClassName: 'font-weight-semi-bold',
        renderCell: ({ row }) => (
          <Box display="flex" flexDirection="column" width="100%" py={0.5} lineHeight={1.3}>   
              <DynamicTextTypography text={row?.description ?? ''} maxLength={106} fontWeight={400} sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}/>
            
            {row?.timeReportTypeName && (
              <span style={{ fontSize: '0.8em', opacity: 0.6, fontWeight: 400 }}>{row.timeReportTypeName}</span>
            )}
          </Box>
        ),
      },
      {
        field: 'fromDateTime',
        headerName: translate('fromDateAndTime'),
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ row }) => <DateTimeStackCell value={row?.fromDateTime} />,
      },
      {
        field: 'toDateTime',
        headerName: translate('toDateAndTime'),
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ row }) => <DateTimeStackCell value={row?.toDateTime} />,
      },
      {
        field: 'duration',
        headerName: translate('duration'),
        flex: 0.5,
        minWidth: 75,
        sortable: false,
        valueGetter: ({ row }) => formatDuration(row?.fromDateTime, row?.toDateTime),
        renderCell: ({ row }) => <DurationCell from={row?.fromDateTime} to={row?.toDateTime} />,
      },
      {
        field: 'resourceName',
        headerName: translate('resource', { ns: 'task' }),
        flex: 0.8,
        minWidth: 120,
        renderCell: row => row.row.resourceName,
      },
    ],
    [translate],
  )

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        headerName: '',
        type: 'actions' as GridNativeColTypes,
        width: 50,
        align: 'center',
        headerAlign: 'center',
        disableColumnMenu: disabledButton,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key={'edit'}
            label={translate('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onUpdate(row)}
            disabled={
              disabledButton ||
              !hasPermission(
                [
                  permissionValue.Locality.Task.Update,
                  permissionValue.Locality.Task.Write,
                  permissionValue.Locality.Task.perform,
                ],
                permission,
              )
            }
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={'delete'}
            label={translate('delete', { ns: 'common' })}
            disabled={
              disabledButton ||
              !hasPermission(
                [
                  permissionValue.Locality.Task.Update,
                  permissionValue.Locality.Task.Write,
                  permissionValue.Locality.Task.perform,
                ],
                permission,
              )
            }
            icon={<MdDelete size={24} />}
            onClick={() => onDelete(row)}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [STATIC_COLUMNS, onDelete, onUpdate, translate, disabledButton],
  )

  return (
    <Box
      bgcolor="white"
      borderRadius="4px"
      {...(rowsToFit?.fill
        ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }
        : { height: height ?? 500 })}
    >
      <InfiniteScrollTable<ITimeReport>
        id={'time-reports'}
        queryUrl={timeReportsEndpoints.getTimeReports(scheduleTaskId)}
        pageSize={20}
        columns={columns}
        defaultColumnOption={columns.map(c => ({ field: c.field, headerName: c.headerName as string }))}
        getRowClassName={params => (params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd')}
        getRowHeight={() => 'auto'}
        autoHeight={false}
        rowsToFit={rowsToFit}
        height={height ?? 500}
        bottomPadding={rowsToFit?.fill ? '0px' : undefined}
        containerProps={{
          ...(rowsToFit?.fill ? {} : { height: height ?? 500 }),
          sx: {
            '& .MuiDataGrid-virtualScrollerContent': {
              width: 'auto !important',
              minWidth: '100% !important',
            },
          },
        }}
        hideDefaultFooterPagination
        onDataChange={onItemsChange}
        emptyComponent={
          <Box height="100%">
            <EmptyStatusCard
              imageWidth={emptyStatusImageWidth ?? '40px'}
              image={NoTimeReports}
              header={emptyStatusTitle}
              content={emptyStatusContent}
              headerFontSize={emptyStatusTitleFontSize}
              contentFontSize={emptyStatusContentFontSize}
              contentClasses={rowsToFit?.fill ? 'mt-0' : 'mt-2'}
              contentSpacing={rowsToFit?.fill ? '6px' : undefined}
              contentTopMargin="10px"
              imageContainerClasses={rowsToFit?.fill ? 'p-0' : 'pb-4 pt-4'}
              height="100%"
            />
          </Box>
        }
      />
    </Box>
  )
}
